import ExpandMoreSharpIcon from '@mui/icons-material/ExpandMoreSharp';
import PlaylistAddCheckSharpIcon from '@mui/icons-material/PlaylistAddCheckSharp';
import { Box, Card, Divider, Grid, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  ControlledTextInput,
  WsAutoComplete,
  WsButton,
  WsColors,
  WsDatePicker,
  WsIconText,
  WsSelect,
  WsTypoColors,
  WsTypoVariants,
  formattedDate,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import { ContactAutocomplete } from '../../../components/ContactAutocomplete';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { ListingSearchPropsForNotesListing } from '../../../features/clientDetails/components/notes/helper/notesHelper';
import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';
import SmartFilter from '../../../features/submissionsNew/components/filters/smart/SmartFilter';
import { getFilterValue, notesDefaultFilterValues } from '../helper/NotesHelper';

interface INotesFilter {
  onSubmit: (filterValues) => void;
  defaultValues?: any;
  defaultSearchFiltersValues: ListingSearchPropsForNotesListing;
  tags: string[];
  toggleParent: string;
  handleArchiveNote: (value) => void;
}

export const StyleBox = styled(Box)(() => ({
  border: '1px solid #E0E0E0',
  borderRadius: 4,
  paddingTop: 5,
  alignItems: 'center',
  backgroundColor: WsColors.TertiaryShades50,
}));

const NotesFilters = ({
  onSubmit,
  defaultSearchFiltersValues,
  tags,
  toggleParent,
  handleArchiveNote,
}: INotesFilter) => {
  const [selectedDate, setSelectedDate] = useState<string>();
  const [tagsFilterValue, setTagsFilterValue] = useState<string[]>([]);
  const methods = useForm({
    defaultValues: { ...defaultSearchFiltersValues },
  });
  const { handleSubmit, reset } = methods;

  const statusArray = [
    {
      label: 'All',
      value: 'ALL',
    },
    {
      label: 'Upcoming',
      value: 'UPCOMING',
    },
    {
      label: 'Overdue',
      value: 'OVERDUE',
    },
    {
      label: 'Closed',
      value: 'CLOSED',
    },
  ];

  const fromDate = methods.watch('fromDate');
  const toDate = methods.watch('toDate');

  const handleReset = () => {
    reset(notesDefaultFilterValues);
    setSelectedDate(undefined);
    onSubmit(notesDefaultFilterValues);
    setTagsFilterValue([]);
  };

  useEffect(() => {
    fromDate &&
      toDate &&
      setSelectedDate(`${formattedDate(`${fromDate}`)} to ${formattedDate(`${toDate}`)}`);
  }, [fromDate, toDate]);

  const onTagsSelection = async (tagsInput: string[]) => {
    setTagsFilterValue(tagsInput);
    methods.setValue('tags', tagsInput);
  };

  const handleApply = () => handleSubmit(onSubmit)();

  return (
    <FormProvider {...methods}>
      <Grid
        container
        sx={{ display: 'flex', backgroundColor: WsColors.White }}
        mb={2}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid item xs={4} p={1}>
          <Card elevation={0} sx={{ p: 1 }}>
            <ControlledTextInput
              fullWidth
              name="search"
              placeholder="Search for Notes"
              size="small"
            />
          </Card>
        </Grid>
        <Grid item xs={1} mt={2} pr={2}>
          <WsButton fullWidth variant="outlined" size="large" type="reset" onClick={handleReset}>
            Clear
          </WsButton>
        </Grid>
        <Grid item xs={1} mt={2} pr={2}>
          <WsButton fullWidth size="large" type="submit">
            Search
          </WsButton>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid
          container
          spacing={1}
          mx={1}
          mb={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item>
            <Grid container spacing={1}>
              <Grid item>
                <SmartFilter
                  filterName="Date"
                  onClick={handleApply}
                  filterValue={selectedDate ?? 'Select Date Range'}
                >
                  <WsDatePicker name="fromDate" label="From Date" disableFuture={true} />
                  <WsDatePicker
                    name="toDate"
                    label="To Date"
                    disableFuture={true}
                    minDate={undefined}
                  />
                </SmartFilter>
              </Grid>
              <Grid item>
                <SmartFilter
                  filterName="Created By :"
                  onClick={handleApply}
                  filterValue={getFilterValue('createdBy', methods, defaultSearchFiltersValues)}
                >
                  <RecruiterSuggestions
                    name="createdBy"
                    multiple={true}
                    defaultValue={methods.getValues('createdBy')}
                  />
                </SmartFilter>
              </Grid>
              <Grid item>
                <SmartFilter
                  filterName="Clients :"
                  onClick={handleApply}
                  filterValue={getFilterValue('clients', methods, defaultSearchFiltersValues)}
                >
                  <ClientAutocomplete name="clients" multiple />
                </SmartFilter>
              </Grid>
              <Grid item>
                <SmartFilter
                  filterName="Contacts :"
                  onClick={handleApply}
                  filterValue={getFilterValue('contacts', methods, defaultSearchFiltersValues)}
                >
                  <ContactAutocomplete name="contacts" multiple />
                </SmartFilter>
              </Grid>
              <Grid item>
                <SmartFilter
                  filterName="Tags :"
                  onClick={handleApply}
                  filterValue={getFilterValue('tags', methods)}
                >
                  <WsAutoComplete
                    onTagsChange={onTagsSelection}
                    placeholder="Eg, Michael"
                    initialValue={tagsFilterValue}
                    value={tagsFilterValue}
                    optionsInStringArray={tags}
                    multiple={true}
                    freeSolo={true}
                    name="tags"
                    size="small"
                  />
                </SmartFilter>
              </Grid>
              <Grid item>
                <SmartFilter
                  filterName="Reminders :"
                  onClick={handleApply}
                  filterValue={getFilterValue('reminderStatus', methods)}
                >
                  <WsSelect
                    placeholder="Eg, Michael"
                    name="reminderStatus"
                    label=""
                    selectArray={statusArray}
                  />
                </SmartFilter>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <PopoverOnClick
              element={
                <StyleBox>
                  <PlaylistAddCheckSharpIcon sx={{ fontSize: 30, color: WsColors.TheBlues500 }} />
                  <ExpandMoreSharpIcon sx={{ color: WsColors.TheBlues500 }} />
                </StyleBox>
              }
            >
              <Box p={2}>
                <WsIconText
                  icon={<></>}
                  text="Active Notes"
                  textCursor="pointer"
                  textColor={
                    toggleParent == 'ACTIVE' ? WsTypoColors.PRIMARY600 : WsTypoColors.BLACK
                  }
                  textSize={WsTypoVariants.SUB_TITLE}
                  onClick={() => handleArchiveNote('ACTIVE')}
                />
              </Box>
              <Box p={2}>
                <WsIconText
                  icon={<></>}
                  text="Archived Notes"
                  textCursor="pointer"
                  textColor={
                    toggleParent != 'ACTIVE' ? WsTypoColors.PRIMARY600 : WsTypoColors.BLACK
                  }
                  textSize={WsTypoVariants.SUB_TITLE}
                  onClick={() => handleArchiveNote('ARCHIVE')}
                />
              </Box>
            </PopoverOnClick>
          </Grid>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default NotesFilters;
